import HideSourceIcon from '@mui/icons-material/HideSource';

import { useAtomValue, useSetAtom } from 'jotai';
import { motion } from 'motion/react';
import { useEffect, useMemo, useState } from 'react';
import { DialogTrigger } from 'react-aria-components';
import { useParams } from 'react-router-dom';

import { InventoryListingStatus } from '@ekhodealer/ekho-common';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Button,
	ContentWithTooltip,
	Drawer,
	Group,
	Stack,
	Tabs,
	toast,
} from '@ekhodealer/ekho-common/rev';
import { transformDisplayName } from '@ekhodealer/ekho-common/utils';

import type { TabItem } from '@ekhodealer/ekho-common/rev';

import { ChevronDown12, ChevronDown24, ChevronUp24 } from '@common/components/icons';
import ArrowsSync24 from '@common/components/icons/ArrowsSync24';
import Bin24 from '@common/components/icons/Bin24';

import { useArchiveInventory } from '../api/useArchiveInventory';
import { useGetInventory } from '../api/useGetInventory';
import { useGetInventoryItem } from '../api/useGetInventoryItem';
import { type SyncInventoryParams, useSyncInventoryMutation } from '../api/useSyncInventory';
import {
	drawerAtom,
	inventoryDetailsNavHandlerAtom,
	isPublishDisabledAtom,
	isSaveDisabledAtom,
	isUnpublishDisabledAtom,
} from '../atoms/editDrawerAtoms';
import { inventoryParamsAtom, InventoryTab } from '../atoms/inventoryAtoms';
import { useInventoryDrawerActions } from '../hooks/useInventoryDrawerActions';
import { useInventoryNavigation } from '../hooks/useInventoryNavigation';
import { buildDrawerStateFromItem, getPublishBlockers } from '../lib/utils';

import { VehicleDetails } from './drawer/VehicleDetails';

export const InventoryDrawer = () => {
	// Get URL params
	const { inventoryItemId, tab } = useParams<{
		inventoryItemId?: string;
		tab?: string;
	}>();
	const {
		closeInventoryDetails,
		switchInventoryDetailsTab,
		navigateToPreviousItem,
		navigateToNextItem,
		getNavigationState,
	} = useInventoryNavigation();

	// drawer is open if inventoryItemId and tab are present in URL
	const isDrawerOpen = Boolean(inventoryItemId && tab);
	const activeTab = (tab as InventoryTab) || InventoryTab.VEHICLE_DETAILS;

	// get inventory item data
	const { data: inventoryItem, isLoading: isInventoryItemLoading } = useGetInventoryItem(
		inventoryItemId || '',
	);

	// Get inventory data to calculate navigation state
	const params = useAtomValue(inventoryParamsAtom);
	const { data: inventoryData } = useGetInventory(params);
	const totalCount = inventoryData?.count ?? 0;

	// Get navigation state for prev/next buttons
	const navigationState = inventoryItemId
		? getNavigationState(inventoryItemId, totalCount)
		: { canGoPrevious: false, canGoNext: false };

	// Drawer actions (save, publish, etc.)
	const { handleSave, handlePublish, handleUnpublish, isSaving, isPublishing, isUnpublishing } =
		useInventoryDrawerActions();
	const isPublishDisabled = useAtomValue(isPublishDisabledAtom);
	const isSaveDisabled = useAtomValue(isSaveDisabledAtom);
	const isUnpublishDisabled = useAtomValue(isUnpublishDisabledAtom);

	// Check if item can be published (no blockers)
	const publishBlockers = useMemo(() => getPublishBlockers(inventoryItem), [inventoryItem]);
	const canPublish = publishBlockers.length === 0 && !isPublishDisabled;

	// Sync and archive mutations
	const syncMutation = useSyncInventoryMutation();
	const { archiveInventory, isArchiving } = useArchiveInventory();
	const [isSyncing, setIsSyncing] = useState(false);

	// Sync selectedItemId from URL params and initialize drawer state from inventory item
	const setDrawerAtom = useSetAtom(drawerAtom);
	useEffect(() => {
		// Initialize drawer state (channel, pricing, etc.) from inventory item when it loads
		if (inventoryItem) {
			const drawerState = buildDrawerStateFromItem(inventoryItem);
			setDrawerAtom({
				...drawerState,
				selectedItemId: inventoryItemId || null, // Use selectedItemId from URL params, not from item
				isDrawerOpen: isDrawerOpen,
			});
		} else {
			// When no item, just sync selectedItemId and isDrawerOpen from URL params
			setDrawerAtom((prev) => ({
				...prev,
				selectedItemId: inventoryItemId || null,
				isDrawerOpen: isDrawerOpen,
			}));
		}
	}, [inventoryItemId, isDrawerOpen, inventoryItem, setDrawerAtom]);

	// navstack handler - used to navigate between tabs
	const navHandler = useAtomValue(inventoryDetailsNavHandlerAtom);

	// tab items configuration
	const tabItems: TabItem[] = [
		{ id: InventoryTab.VEHICLE_DETAILS, content: 'Vehicle Details' },
		// { id: InventoryTab.ACCESSORIES, content: 'Accessories' },
	];

	const handleTabSelect = (item: TabItem) => {
		if (!inventoryItemId) return;
		switchInventoryDetailsTab(inventoryItemId, item.id as InventoryTab);
	};

	// Navigation handlers
	const handlePreviousItem = () => {
		if (!inventoryItemId) return;
		navigateToPreviousItem(inventoryItemId, activeTab);
	};

	const handleNextItem = () => {
		if (!inventoryItemId) return;
		navigateToNextItem(inventoryItemId, activeTab);
	};

	// Action handlers
	const handleSaveAndPublish = () => {
		if (canPublish) {
			handlePublish();
		} else {
			handleSave();
		}
	};

	const handleSyncInventory = async () => {
		if (!inventoryItem?.id || isInventoryItemLoading) {
			toast({
				message: 'Cannot sync: missing required data',
				variant: 'error',
			});
			return;
		}

		// Check for vehicleDetailsUrl or scrapeUrl (required for syncing)
		const vehicleDetailsUrl =
			inventoryItem.vehicleDetailsUrl ||
			inventoryItem.scrapingConfig?.INVENTORY_ITEM_SYNC?.scrapeUrl;

		if (!vehicleDetailsUrl) {
			toast({
				message: 'Cannot sync: missing scrape details',
				variant: 'error',
			});
			return;
		}

		setIsSyncing(true);
		try {
			const params: SyncInventoryParams = {
				servicesProviderId: inventoryItem.ownerId,
				inventoryIds: [inventoryItem.id],
			};

			await syncMutation.mutateAsync(params);
			toast({
				message: 'Sync initiated successfully',
				variant: 'success',
			});
		} catch (error) {
			toast({
				message: 'Failed to sync inventory item',
				variant: 'error',
			});
			console.error('Sync error:', error);
		} finally {
			setIsSyncing(false);
		}
	};

	const handleArchiveInventory = async () => {
		if (!inventoryItem?.id) {
			toast({
				message: 'Cannot archive: missing inventory item ID',
				variant: 'error',
			});
			return;
		}

		try {
			await archiveInventory([inventoryItem.id], 'Archived from drawer');
			toast({
				message: 'Inventory item archived successfully',
				variant: 'success',
			});
			closeInventoryDetails();
		} catch (error) {
			toast({
				message: 'Failed to archive inventory item',
				variant: 'error',
			});
			console.error('Archive error:', error);
		}
	};

	// render the appropriate content based on the active tab - tabs will only show if there is more than 1
	const renderTabContent = () => {
		switch (activeTab) {
			case InventoryTab.VEHICLE_DETAILS:
				return <VehicleDetails />;
		}
	};

	// don't render if drawer is not open
	if (!isDrawerOpen) {
		return null;
	}

	// Get drawer title - show placeholder while loading
	const drawerTitle = inventoryItem
		? transformDisplayName(
				`${inventoryItem.modelYear} ${inventoryItem.make} ${inventoryItem.model}`,
			)
		: 'Loading...';

	return (
		<DialogTrigger
			isOpen={isDrawerOpen}
			onOpenChange={(open) => {
				if (!open) {
					closeInventoryDetails();
				}
			}}>
			<Drawer
				size='large'
				placement='right'
				hasCloseButton={true}
				isDismissable={true}
				isKeyboardDismissDisabled={false}
				ariaLabel='Inventory Details'>
				<Drawer.Header>{drawerTitle}</Drawer.Header>
				<Drawer.Content>
					<Stack>
						{inventoryItem && tabItems.length > 1 && (
							<Tabs
								items={tabItems}
								selectedKey={activeTab}
								onSelect={handleTabSelect}
								className='inventory-details-tabs--custom'
							/>
						)}
						{renderTabContent()}
					</Stack>
				</Drawer.Content>
				<Drawer.Footer>
					<motion.div
						key={String(navHandler.currentViewId)}
						initial={{ opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.2, ease: 'easeOut' }}
						className='drawer-footer'>
						<Group itemsSpacing='8' itemsAlignX='space-between' noWrap>
							<Group.Item>
								<ActionMenuTrigger>
									<Button
										variant='secondary'
										size='large'
										icon={<ChevronDown12 />}
										iconSide='right'>
										More actions
									</Button>
									<ActionMenu>
										<ActionMenuItem
											label='Sync inventory item'
											leadingAccessory={<ArrowsSync24 />}
											onAction={handleSyncInventory}
											disabled={
												isSyncing ||
												isInventoryItemLoading ||
												!inventoryItem
											}
										/>
										<ActionMenuItem
											label='Archive inventory item'
											leadingAccessory={<Bin24 />}
											onAction={handleArchiveInventory}
											disabled={
												isArchiving ||
												isInventoryItemLoading ||
												!inventoryItem
											}
										/>
									</ActionMenu>
								</ActionMenuTrigger>
							</Group.Item>
							<Group itemsSpacing='8' noWrap>
								{/* Unpublish button - only show when published */}
								{inventoryItem?.listing?.status ===
									InventoryListingStatus.PUBLISHED && (
									<ContentWithTooltip
										tooltipContent='Unpublish'
										content={
											<Button
												variant='secondary'
												size='large'
												onPress={handleUnpublish}
												aria-label='Unpublish'
												isDisabled={
													isUnpublishDisabled ||
													isInventoryItemLoading ||
													isSaving ||
													isPublishing
												}
												isLoading={isUnpublishing}>
												<HideSourceIcon />
											</Button>
										}
									/>
								)}
								<Button
									variant='filled'
									size='large'
									onPress={handleSaveAndPublish}
									isDisabled={
										isInventoryItemLoading ||
										(canPublish ? isPublishDisabled : isSaveDisabled) ||
										isUnpublishing
									}
									isLoading={canPublish ? isPublishing : isSaving}>
									{canPublish ? 'Save & publish' : 'Save'}
								</Button>
								{navigationState.canGoPrevious && (
									<Button
										variant='secondary'
										size='icon'
										icon={<ChevronUp24 />}
										aria-label='Previous inventory item'
										onPress={handlePreviousItem}
									/>
								)}
								{navigationState.canGoNext && (
									<Button
										variant='secondary'
										size='icon'
										icon={<ChevronDown24 />}
										aria-label='Next inventory item'
										onPress={handleNextItem}
									/>
								)}
							</Group>
						</Group>
					</motion.div>
				</Drawer.Footer>
			</Drawer>
		</DialogTrigger>
	);
};
